/**
 * Hook for PostgreSQL restore operations using pg_restore or psql.
 * Uses TanStack Query mutation for state management.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { pgsqlRestore, pgsqlCheckRestoreTools } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import type { PgsqlRestoreRequest, PgsqlRestoreResult, PgsqlRestoreFormat } from '../types/backupRestore';
import { useAnalytics } from '../../../core/analytics';

type UsePostgresRestoreOptions = {
  onSuccess?: (result: PgsqlRestoreResult, operationId: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for restoring a PostgreSQL database using pg_restore or psql.
 */
export function usePostgresRestore(options: UsePostgresRestoreOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();
  const [detectedFormat, setDetectedFormat] = useState<PgsqlRestoreFormat | null>(null);
  const { track } = useAnalytics();

  const mutation = useMutation({
    mutationFn: async (request: PgsqlRestoreRequest) => {
      // Track restore initiation
      track('feature:backup_restore', { engine: 'pgsql' });

      // Add operation to store (it generates id, status, startedAt)
      const operationId = addOperation({
        type: 'pgsql-restore',
        connectionId: request.connectionId,
        databaseName: request.targetDatabaseName,
        filePath: request.filePath || '',
      });

      try {
        // Update status to in progress
        updateOperation(operationId, { status: 'inProgress', message: 'Restore starting...' });

        const result = await pgsqlRestore(request);

        // Store detected format if available
        if (result.detectedFormat) {
          setDetectedFormat(result.detectedFormat);
        }

        // Update operation status
        updateOperation(operationId, {
          status: result.success ? 'succeeded' : 'failed',
          completedAt: new Date().toISOString(),
          error: result.error,
        });

        return { result, operationId };
      } catch (error) {
        // Update operation status on error
        updateOperation(operationId, {
          status: 'failed',
          completedAt: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    },
    onSuccess: ({ result, operationId }) => {
      options.onSuccess?.(result, operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    restoreDatabase: (request: PgsqlRestoreRequest) => mutation.mutate(request),
    isRestoring: mutation.isPending,
    error: mutation.error,
    reset: () => {
      mutation.reset();
      setDetectedFormat(null);
    },
    detectedFormat,
  };
}

/**
 * Hook for checking if pg_restore/psql tools are available.
 * @param customBinPath Optional custom path to PostgreSQL bin directory
 */
export function usePgRestoreAvailability(customBinPath?: string) {
  const query = useQuery({
    queryKey: ['pgsql-restore-tools-availability', customBinPath],
    queryFn: () => pgsqlCheckRestoreTools(customBinPath),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });

  return {
    isAvailable: query.data?.available ?? false,
    pgRestorePath: query.data?.pgRestorePath,
    psqlPath: query.data?.psqlPath,
    error: query.data?.error,
    isChecking: query.isLoading,
    refetch: query.refetch,
  };
}
